import { FC, useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Input, Checkbox, Radio, Spin } from "antd";
import Button from "@lhd/components/Button";
import { toHalfWidth } from "@lhd/features/Salary/SalarySheets/components/exportPDF/utility";
import { BsSearch } from "react-icons/bs";
import {
  FilterCellWrapper,
  FilterSection,
  FilterTitle,
  FilterOptionsContainer,
  FilterOptionItem,
  EmptyFilterText,
  CheckboxGroupWrapper,
  ScrollBarContainer,
  ScrollBarInner,
  FilterOptionsWrapper,
} from "./styled";
import { get, isEmpty } from "lodash";
import CustomVerticalScrollBar from "@lhd/components/CustomVerticalScrollBar";
import CustomHorizontalScrollBar from "@lhd/components/CustomHorizontalScrollBar";
import DateHierarchyFilter, {
  formatRawDateToAPI,
} from "@lhd/components/DateTree";
import { APP_PART_TYPES } from "@lhd/features/Apps/AppForm/constants";

const Date_Tree = [
  APP_PART_TYPES.DATE,
  APP_PART_TYPES.DATE_AND_TIME,
  APP_PART_TYPES.TIME,
  APP_PART_TYPES.SIGNED_DATE,
  APP_PART_TYPES.CREATED_DATETIME,
  APP_PART_TYPES.UPDATED_DATETIME,
] as string[];

const isFormattedDateLabel = (label: string): boolean => {
  // Match both "YYYY年MM月DD日" and "YYYY年MM月" (for career_qualifications)
  return /^\d{4}年\d{2}月(\d{2}日)?/.test(label);
};

interface FilterCellProps {
  columnKey: string;
  filterOptions: any[];
  onFilter: (columnKey: string, filterData: any) => void;
  onCancel: () => void;
  initialFilter?: any;
  displaySize?: string;
  hideSpecialEmptyCheckbox?: boolean;
  type?: string;
  isLoading?: boolean;
  isApiFormat?: boolean;
}

const FilterCell: FC<FilterCellProps> = ({
  columnKey,
  filterOptions,
  onFilter,
  onCancel,
  initialFilter = {},
  displaySize,
  hideSpecialEmptyCheckbox = true,
  type,
  isLoading = false,
  isApiFormat = false,
}) => {
  const getOptionValue = useCallback(
    (item: any) => {
      if (typeof item === "object" && item !== null) {
        if ("value" in item) return item.value?.toString() || "";
        return get(item, columnKey)?.toString() || "";
      }
      return item?.toString() || "";
    },
    [columnKey],
  );

  const getOptionLabel = useCallback(
    (item: any) => {
      if (typeof item === "object" && item !== null) {
        if ("label" in item) return item.label?.toString() || "";
        return get(item, columnKey)?.toString() || "";
      }
      return item?.toString() || "";
    },
    [columnKey],
  );

  const getFormattedValue = useCallback(
    (opt: any, isDateField: boolean) => {
      if (isDateField) {
        const val = getOptionValue(opt);
        return isApiFormat
          ? formatRawDateToAPI(val, type) || val
          : getOptionLabel(opt);
      }
      return getOptionValue(opt);
    },
    [getOptionValue, getOptionLabel, isApiFormat, type],
  );

  const [searchValue, setSearchValue] = useState(initialFilter.search || "");
  const [sortValue, setSortValue] = useState(initialFilter.sort || "");
  const [filterValues, setFilterValues] = useState<string[]>(() => {
    if (initialFilter.filter) {
      if (Array.isArray(initialFilter.filter)) {
        return initialFilter.filter;
      }
      if (
        typeof initialFilter.filter === "object" &&
        initialFilter.filter !== null
      ) {
        return Object.keys(initialFilter.filter).filter(
          (k) => initialFilter.filter[k] === true,
        );
      }
    }
    if (initialFilter.isCheckAll) {
      // Check if this is a date field to use formatted labels
      let isDateFieldInit =
        filterOptions.length > 0 && Date_Tree.includes(type || "");
      // Also detect native date fields by label format
      if (!isDateFieldInit && filterOptions.length > 0) {
        const sampleLabel = getOptionLabel(filterOptions[0]);
        isDateFieldInit = isFormattedDateLabel(sampleLabel);
      }
      const values = filterOptions.map((opt) =>
        getFormattedValue(opt, isDateFieldInit),
      );
      return values;
    }
    return [];
  });
  const [filterSpecialEmpty, setFilterSpecialEmpty] = useState(() => {
    if ("filter_special_empty" in initialFilter)
      return initialFilter.filter_special_empty;
    return !hideSpecialEmptyCheckbox;
  });
  const [isSelectAllClicked, setIsSelectAllClicked] = useState(false);

  const isDirtyRef = useRef(false);
  const autoFilledColumnKey = useRef<string | null>(null);

  useEffect(() => {
    isDirtyRef.current = false;
  }, [columnKey]);

  const lastSyncedFilterStr = useRef("");

  useEffect(() => {
    if (isDirtyRef.current) return;

    const initialFilterStr = JSON.stringify(initialFilter);
    const signature = columnKey ? `${columnKey}_${filterOptions.length}` : null;

    if (initialFilter.filter || initialFilter.isCheckAll) {
      if (Object.keys(initialFilter).length > 0) {
        if (initialFilter.isCheckAll) {
          let isDateFieldAutoFill = Date_Tree.includes(type || "");
          if (!isDateFieldAutoFill && filterOptions.length > 0) {
            const sampleLabel = getOptionLabel(filterOptions[0]);
            isDateFieldAutoFill = isFormattedDateLabel(sampleLabel);
          }
          setFilterValues(
            filterOptions.map(
              isDateFieldAutoFill ? getOptionLabel : getOptionValue,
            ),
          );
          setIsSelectAllClicked(true);
        } else if (initialFilter.filter) {
          let filterLen = 0;
          if (Array.isArray(initialFilter.filter)) {
            setFilterValues(initialFilter.filter);
            filterLen = initialFilter.filter.length;
          } else if (
            typeof initialFilter.filter === "object" &&
            initialFilter.filter !== null
          ) {
            const arr = Object.keys(initialFilter.filter).filter(
              (k) => initialFilter.filter[k] === true,
            );
            setFilterValues(arr);
            filterLen = arr.length;
          }
          if (filterLen === filterOptions.length) {
            setIsSelectAllClicked(true);
          }
        }
        if ("filter_special_empty" in initialFilter) {
          setFilterSpecialEmpty(initialFilter.filter_special_empty);
        } else {
          setFilterSpecialEmpty(!hideSpecialEmptyCheckbox);
        }
        setSearchValue(initialFilter.search || "");
        setSortValue(initialFilter.sort || "");
        lastSyncedFilterStr.current = initialFilterStr;
        autoFilledColumnKey.current = signature;
      }
    } else if (
      filterOptions.length > 0 &&
      autoFilledColumnKey.current !== signature
    ) {
      // Use formatted labels for date fields, raw values for others
      let isDateFieldAutoFill = Date_Tree.includes(type || "");
      // Also detect native date fields by label format
      if (!isDateFieldAutoFill && filterOptions.length > 0) {
        const sampleLabel = getOptionLabel(filterOptions[0]);
        isDateFieldAutoFill = isFormattedDateLabel(sampleLabel);
      }
      const valuesToSet = filterOptions.map((opt) =>
        getFormattedValue(opt, isDateFieldAutoFill),
      );
      setFilterValues(valuesToSet);
      if (!("filter_special_empty" in initialFilter)) {
        setFilterSpecialEmpty(!hideSpecialEmptyCheckbox);
      }
      setSearchValue(initialFilter.search || "");
      setSortValue(initialFilter.sort || "");
      autoFilledColumnKey.current = signature;
      lastSyncedFilterStr.current = initialFilterStr;
    } else if (filterOptions.length === 0 && !hideSpecialEmptyCheckbox) {
      // No regular options, only empty special option. Auto-select it.
      setFilterSpecialEmpty(true);
      setIsSelectAllClicked(true);
    }
  }, [
    filterOptions,
    initialFilter,
    hideSpecialEmptyCheckbox,
    columnKey,
    getOptionLabel,
    getOptionValue,
  ]);

  const filterWrapperRef = useRef<HTMLDivElement>(null);
  const filterOptionsRef = useRef<HTMLDivElement>(null);
  const [filterWrapperElement, setFilterWrapperElement] =
    useState<HTMLElement | null>(null);
  const [filterOptionsElement, setFilterOptionsElement] =
    useState<HTMLElement | null>(null);

  const filteredOptions = useMemo(() => {
    return filterOptions;
  }, [filterOptions]);

  // Detect if this is a date field (native date or app date)
  const isDateField = useMemo(() => {
    if (Date_Tree.includes(type || "")) {
      return true;
    }

    // For native date fields, detect from label format
    // Labels are formatted as "2025年11月25日" by useGetOptions
    if (filterOptions.length > 0) {
      const sampleLabel = getOptionLabel(filterOptions[0]);
      const isDate = isFormattedDateLabel(sampleLabel);
      return isDate;
    }

    return false;
  }, [type, filterOptions, columnKey, getOptionLabel]);

  // Update filterWrapperElement when ref is set
  useEffect(() => {
    if (filterWrapperRef.current) {
      setFilterWrapperElement(filterWrapperRef.current);
    }
  }, [filteredOptions.length, searchValue, sortValue]);

  // Update filterOptionsElement when ref is set
  useEffect(() => {
    if (filterOptionsRef.current) {
      setFilterOptionsElement(filterOptionsRef.current);
    }
  }, [filteredOptions.length]);

  const handleFilter = () => {
    const filterData: any = {};
    filterData.search = searchValue || "";
    filterData.sort = sortValue || "";
    filterData.filter = filterValues;
    filterData.filter_special_empty = filterSpecialEmpty;

    const isCheckAll =
      (isSelectAllClicked || filterValues.length === filterOptions.length) &&
      (hideSpecialEmptyCheckbox || filterSpecialEmpty !== false);

    if (isCheckAll) {
      delete filterData.filter;
      delete filterData.filter_special_empty;
      filterData.isCheckAll = true;
    } else {
      filterData.isCheckAll = undefined;
    }

    // Pass filterOptions to map search results locally
    filterData.filterOptions = filterOptions;

    onFilter(columnKey, filterData);
  };

  const handleClear = () => {
    setSearchValue("");
    setSortValue("");
    setFilterValues([]);
    setFilterSpecialEmpty(false);
    setIsSelectAllClicked(false);
  };

  const handleSelectAll = () => {
    // Use formatted labels for date fields, raw values for others
    let isDateFieldSelectAll = Date_Tree.includes(type || "");
    // Also detect native date fields by label format
    if (!isDateFieldSelectAll && filterOptions.length > 0) {
      const sampleLabel = getOptionLabel(filterOptions[0]);
      isDateFieldSelectAll = isFormattedDateLabel(sampleLabel);
    }
    const allValues = filterOptions.map((opt) =>
      getFormattedValue(opt, isDateFieldSelectAll),
    );
    setFilterValues(allValues);
    setFilterSpecialEmpty(!hideSpecialEmptyCheckbox);
    setIsSelectAllClicked(true);
  };

  return (
    <FilterCellWrapper
      $displaySize={displaySize}
      onChangeCapture={() => {
        isDirtyRef.current = true;
      }}
    >
      <FilterOptionsWrapper>
        <div className="filter-wrapper" ref={filterWrapperRef}>
          <FilterSection className="sort">
            <FilterTitle className="title">ソート</FilterTitle>
            <Radio.Group
              value={sortValue}
              onChange={(e) => setSortValue(e.target.value)}
              options={[
                { label: "なし", value: "" },
                { label: "昇順 (A->Z)", value: "asc" },
                { label: "降順 (Z->A)", value: "desc" },
              ]}
            />
          </FilterSection>

          <FilterSection>
            <FilterTitle className="title">検索</FilterTitle>
            <Input
              suffix={<BsSearch />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onBlur={(e) => {
                if (Date_Tree.includes(type || "")) {
                  const halfWidthValue = toHalfWidth(e?.target?.value || "");
                  setSearchValue(halfWidthValue);
                }
              }}
              onPressEnter={(e) => {
                if (Date_Tree.includes(type || "")) {
                  const halfWidthValue = toHalfWidth(
                    e.currentTarget.value || "",
                  );
                  setSearchValue(halfWidthValue);
                }
                handleFilter();
              }}
              placeholder="検索"
            />
          </FilterSection>

          <FilterSection className="filter">
            <FilterTitle className="title">フィルター</FilterTitle>

            {!hideSpecialEmptyCheckbox && (
              <div
                style={{
                  margin: "0px",
                  minHeight: "18px",
                  marginBottom: "4px",
                }}
              >
                <Checkbox
                  checked={filterSpecialEmpty}
                  onChange={(e) => setFilterSpecialEmpty(e.target.checked)}
                >
                  (空白)
                </Checkbox>
              </div>
            )}
            <Spin spinning={!!isLoading}>
              {!isEmpty(filteredOptions) ? (
                <FilterOptionsWrapper>
                  <FilterOptionsContainer
                    ref={filterOptionsRef}
                    className="filter-options scroll"
                    style={{ maxHeight: "140px" }}
                  >
                    <CheckboxGroupWrapper>
                      {isDateField ? (
                        <DateHierarchyFilter
                          key={`${columnKey}-${filteredOptions.length}`}
                          mode={
                            [
                              "date",
                              "date_and_time",
                              "time",
                              "created_datetime",
                              "updated_datetime",
                            ].includes(type || "")
                              ? type
                              : "default"
                          }
                          timestamps={filteredOptions.map(getOptionLabel)}
                          initialChecked={filterValues}
                          onChange={(values, _originalValues, apiKeys) => {
                            setFilterValues(
                              isApiFormat ? apiKeys || values : values,
                            );
                            setIsSelectAllClicked(false);
                          }}
                        />
                      ) : (
                        <Checkbox.Group
                          value={filterValues}
                          onChange={(values) => {
                            setFilterValues(values as string[]);
                            setIsSelectAllClicked(false);
                          }}
                        >
                          {filteredOptions.map((item) => {
                            const value = getOptionValue(item);
                            const label = getOptionLabel(item);
                            return (
                              <FilterOptionItem key={value}>
                                <Checkbox value={value}>{label}</Checkbox>
                              </FilterOptionItem>
                            );
                          })}
                        </Checkbox.Group>
                      )}
                    </CheckboxGroupWrapper>
                  </FilterOptionsContainer>
                  <ScrollBarContainer $position="vertical">
                    <ScrollBarInner>
                      <CustomVerticalScrollBar element={filterOptionsElement} />
                    </ScrollBarInner>
                  </ScrollBarContainer>
                  <ScrollBarContainer $position="horizontal">
                    <ScrollBarInner>
                      <CustomHorizontalScrollBar
                        element={filterOptionsElement}
                      />
                    </ScrollBarInner>
                  </ScrollBarContainer>
                </FilterOptionsWrapper>
              ) : hideSpecialEmptyCheckbox ? (
                <EmptyFilterText className="title">空</EmptyFilterText>
              ) : null}
            </Spin>
          </FilterSection>

          <div className="action">
            <p onClick={handleSelectAll}>すべて選択</p>
            <span>-</span>
            <p onClick={handleClear}>クリア</p>
          </div>

          <div className="footer">
            <Button
              className="btn btn-filter"
              label="保存"
              onClick={handleFilter}
            />
            <Button
              className="btn"
              type="cancel-btn"
              label="キャンセル"
              onClick={onCancel}
            />
          </div>
        </div>
        <ScrollBarContainer $position="vertical">
          <ScrollBarInner>
            <CustomVerticalScrollBar element={filterWrapperElement} />
          </ScrollBarInner>
        </ScrollBarContainer>
        <ScrollBarContainer $position="horizontal">
          <ScrollBarInner>
            <CustomHorizontalScrollBar element={filterWrapperElement} />
          </ScrollBarInner>
        </ScrollBarContainer>
      </FilterOptionsWrapper>
    </FilterCellWrapper>
  );
};

export default FilterCell;
